import { useState } from 'react'
import Add from '@mui/icons-material/Add'
import RuleOutlined from '@mui/icons-material/RuleOutlined'
import {
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useGetApprovalPolicies } from '@/service-library/hooks/approval-policies'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import { useProjectContext } from '../project-tables/ProjectProvider'
import ApprovalPolicyCard from './ApprovalPolicyCard'
import ApprovalPolicyEditor from './ApprovalPolicyEditor'

// 'new' opens a fresh editor at the top of the list; a policy id opens that
// policy's editor in place of its card. Only one of either at a time.
type EditingState = string | 'new' | null

function EmptyState() {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
      <Stack
        alignItems="center"
        spacing={0.5}
        sx={{ textAlign: 'center', px: 2, py: 6 }}
      >
        <RuleOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          No approval policies yet
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Create one to require sign-off on matching documents.
        </Typography>
      </Stack>
    </Paper>
  )
}

export default function ApprovalPolicyList() {
  const { project } = useProjectContext()
  const [editing, setEditing] = useState<EditingState>(null)

  const {
    approvalPolicies = [],
    isPending,
    queryKey,
  } = useGetApprovalPolicies({
    filters: { limit: '1000', project_id: project.id },
  })

  return (
    <SettingsPage
      title="Approvals"
      description="Decide when a document needs sign-off before it can move on through the workflow. Every policy is checked against each document in this project."
      actions={
        // Hidden while loading: the editor only renders once policies load,
        // so a click here would do nothing visible.
        !isPending && (
          <Button
            variant="text"
            startIcon={<Add />}
            onClick={() => setEditing('new')}
          >
            New policy
          </Button>
        )
      }
    >
      {isPending ? (
        <Stack alignItems="center" sx={{ p: 4 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack spacing={2}>
          {editing === 'new' && (
            <ApprovalPolicyEditor
              listQueryKey={queryKey}
              onClose={() => setEditing(null)}
            />
          )}

          {approvalPolicies.length === 0 && editing === null && <EmptyState />}

          {approvalPolicies.map((policy) =>
            editing === policy.id ? (
              <ApprovalPolicyEditor
                key={policy.id}
                policy={policy}
                listQueryKey={queryKey}
                onClose={() => setEditing(null)}
              />
            ) : (
              <ApprovalPolicyCard
                key={policy.id}
                policy={policy}
                listQueryKey={queryKey}
                onEdit={() => setEditing(policy.id)}
              />
            ),
          )}
        </Stack>
      )}
    </SettingsPage>
  )
}
